import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Lock } from "lucide-react";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { SUPPORTED_CURRENCIES } from "@/api/currency";
import useSubscription from "@/contexts/SubscriptionContext";
import { canSelectCurrency, isPremium } from "@/lib/featureGates";
import { useTranslation } from "react-i18next";

const CurrencyPreferences: React.FC = () => {
  const { preferences, isLoading, updatePreferences, availableCurrencies } = useUserPreferences();
  const { status } = useSubscription();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const userIsPremium = isPremium(status);

  // Create local state for form values
  const [localPreferences, setLocalPreferences] = useState({
    base_currency: preferences?.base_currency || "TRY",
    base_official_divide: preferences?.base_official_divide || false,
    second_currency: preferences?.second_currency || "USD",
    second_official_divide: preferences?.second_official_divide || false,
    third_currency: preferences?.third_currency || "EUR",
    third_official_divide: preferences?.third_official_divide || false,
  });

  // Update local state when preferences are loaded
  React.useEffect(() => {
    if (preferences) {
      setLocalPreferences({
        base_currency: preferences.base_currency,
        base_official_divide: preferences.base_official_divide,
        second_currency: preferences.second_currency,
        second_official_divide: preferences.second_official_divide,
        third_currency: preferences.third_currency,
        third_official_divide: preferences.third_official_divide,
      });
    }
  }, [preferences]);

  // Handle value changes
  const handleCurrencyChange = (value: string, field: 'base_currency' | 'second_currency' | 'third_currency') => {
    setLocalPreferences({
      ...localPreferences,
      [field]: value,
    });
  };

  // Handle toggle changes
  const handleToggleChange = (checked: boolean, field: 'base_official_divide' | 'second_official_divide' | 'third_official_divide') => {
    setLocalPreferences({
      ...localPreferences,
      [field]: checked,
    });
  };

  // Save changes
  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await updatePreferences(localPreferences);
    } finally {
      setSaving(false);
    }
  };

  // Find currency label by value
  const getCurrencyLabel = (value: string) => {
    const currency = SUPPORTED_CURRENCIES.find(c => c.value === value);
    return currency ? `${currency.label} (${currency.symbol})` : value;
  };

  // Disable saving if nothing has changed
  const hasChanges = preferences && (
    preferences.base_currency !== localPreferences.base_currency ||
    preferences.base_official_divide !== localPreferences.base_official_divide ||
    preferences.second_currency !== localPreferences.second_currency ||
    preferences.second_official_divide !== localPreferences.second_official_divide ||
    preferences.third_currency !== localPreferences.third_currency ||
    preferences.third_official_divide !== localPreferences.third_official_divide
  );

  // Display loading state while preferences are loading
  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Base Currency */}
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <Label htmlFor="base-currency">{t('currency.base_currency')}</Label>
          <Select
            value={localPreferences.base_currency}
            onValueChange={(value) => handleCurrencyChange(value, 'base_currency')}
          >
            <SelectTrigger id="base-currency">
              <SelectValue placeholder={t('currency.select_currency')} />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map((currency) => {
                const allowed = canSelectCurrency(currency, status, localPreferences.base_currency);
                return (
                  <SelectItem
                    key={currency}
                    value={currency}
                    disabled={!allowed}
                    title={!allowed && !userIsPremium ? 'Premium gerekli' : undefined}
                  >
                    <span className="flex items-center gap-2">
                      <span>{getCurrencyLabel(currency)}</span>
                      {!allowed && <Lock className="h-3 w-3 opacity-60" />}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="base-official-divide" className="cursor-pointer">
            {t('currency.official_unofficial_separation')}
          </Label>
          <Switch
            id="base-official-divide"
            checked={localPreferences.base_official_divide}
            onCheckedChange={(checked) => handleToggleChange(checked, 'base_official_divide')}
          />
        </div>
      </div>

      {/* Second Currency */}
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <Label htmlFor="second-currency">{t('currency.second_currency')}</Label>
          <Select
            value={localPreferences.second_currency}
            onValueChange={(value) => handleCurrencyChange(value, 'second_currency')}
          >
            <SelectTrigger id="second-currency">
              <SelectValue placeholder={t('currency.select_currency')} />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map((currency) => {
                const allowed = canSelectCurrency(currency, status, localPreferences.second_currency);
                return (
                  <SelectItem
                    key={currency}
                    value={currency}
                    disabled={!allowed}
                    title={!allowed && !userIsPremium ? 'Premium gerekli' : undefined}
                  >
                    <span className="flex items-center gap-2">
                      <span>{getCurrencyLabel(currency)}</span>
                      {!allowed && <Lock className="h-3 w-3 opacity-60" />}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="second-official-divide" className="cursor-pointer">
            {t('currency.official_unofficial_separation')}
          </Label>
          <Switch
            id="second-official-divide"
            checked={localPreferences.second_official_divide}
            onCheckedChange={(checked) => handleToggleChange(checked, 'second_official_divide')}
          />
        </div>
      </div>

      {/* Third Currency */}
      <div className="space-y-4">
        <div className="flex flex-col space-y-2">
          <Label htmlFor="third-currency">{t('currency.third_currency')}</Label>
          <Select
            value={localPreferences.third_currency}
            onValueChange={(value) => handleCurrencyChange(value, 'third_currency')}
          >
            <SelectTrigger id="third-currency">
              <SelectValue placeholder={t('currency.select_currency')} />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map((currency) => {
                const allowed = canSelectCurrency(currency, status, localPreferences.third_currency);
                return (
                  <SelectItem
                    key={currency}
                    value={currency}
                    disabled={!allowed}
                    title={!allowed && !userIsPremium ? 'Premium gerekli' : undefined}
                  >
                    <span className="flex items-center gap-2">
                      <span>{getCurrencyLabel(currency)}</span>
                      {!allowed && <Lock className="h-3 w-3 opacity-60" />}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="third-official-divide" className="cursor-pointer">
            {t('currency.official_unofficial_separation')}
          </Label>
          <Switch
            id="third-official-divide"
            checked={localPreferences.third_official_divide}
            onCheckedChange={(checked) => handleToggleChange(checked, 'third_official_divide')}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-4">
        <Button 
          className="w-full" 
          disabled={saving || !hasChanges} 
          onClick={handleSaveChanges}
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('currency.saving')}
            </>
          ) : (
            t('currency.save_changes')
          )}
        </Button>
      </div>
    </div>
  );
};

export default CurrencyPreferences; 